import React, { useMemo } from 'react';
import ServerDataGrid from '../../Components/ServerDataGrid';
import useBreakpoint from '../hooks/useBreakpoint';

/** Ordered smallest → largest, matching MUI defaults. */
const BREAKPOINT_ORDER = ['xs', 'sm', 'md', 'lg', 'xl'];

/**
 * ResponsiveDataGrid — drop-in replacement for ServerDataGrid on pages that
 * want responsive column visibility and mobile-adaptive behaviour.
 *
 * New props vs ServerDataGrid:
 *
 *   columns        — same as before, but each column can carry:
 *                      `minVisible: 'xs' | 'sm' | 'md' | 'lg' | 'xl'`
 *                    Columns without `minVisible` are always visible.
 *
 *   pageSizeOptions — mobile page-size is auto-capped at the first option ≤ 5.
 *
 * The grid always uses `fill` mode — it stretches to fill its parent, which
 * must be a `<PageContent fill>` container (flex: 1 1 auto; min-height: 0).
 *
 * All other ServerDataGrid props are passed through as-is.
 *
 * Usage:
 *   const columns = [
 *     { field: 'invoiceNumber', headerName: 'Invoice #', minVisible: 'xs', flex: 0.5 },
 *     { field: 'customerName',  headerName: 'Customer',  minVisible: 'xs', flex: 0.5 },
 *     { field: 'dueDate',       headerName: 'Due Date',  minVisible: 'sm', flex: 0.5 },
 *     { field: 'terms',         headerName: 'Terms',     minVisible: 'md', flex: 0.5 },
 *   ];
 *
 *   <PageContent fill>
 *     <ResponsiveDataGrid columns={columns} fetchData={fetchFn} ... />
 *   </PageContent>
 */
export default function ResponsiveDataGrid({
  columns,
  initialPageSize = 10,
  pageSizeOptions,
  additionalProps = {},
  clientSort,
  initialSortModel,
  ...rest
}) {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
  const currentIdx = BREAKPOINT_ORDER.indexOf(breakpoint);

  // DataGrid v5.2 doesn't support the `columnVisibilityModel` prop — column
  // visibility must be driven by `hide: true` on the column definition itself.
  // We also strip `minVisible` so MUI doesn't emit "unknown prop" warnings.
  const cleanColumns = useMemo(() => {
    return columns.map(({ minVisible, ...col }) => {
      if (minVisible) {
        const minIdx = BREAKPOINT_ORDER.indexOf(minVisible);
        return { ...col, hide: currentIdx < minIdx };
      }
      return col;
    });
  }, [columns, currentIdx]);

  const effectivePageSize = isMobile
    ? Math.min(initialPageSize, 10)
    : initialPageSize;

  const effectivePageSizeOptions = pageSizeOptions ?? [10, 25, 50, 100];

  return (
    <ServerDataGrid
      columns={cleanColumns}
      initialPageSize={effectivePageSize}
      rowsPerPageOptions={effectivePageSizeOptions}
      fill
      clientSort={clientSort}
      initialSortModel={initialSortModel}
      additionalProps={{
        density: isMobile ? 'compact' : 'standard',
        ...additionalProps,
      }}
      {...rest}
    />
  );
}
